/**
 * The theme contract — **version 1**.
 *
 * Spec §6.4.3, rule 2: "Rungs 1–4 ride a versioned theme contract — the token
 * names, section registry and slot ids form an interface with a version, like
 * the catalog and the playbook. We keep one version of backward compatibility;
 * a breaking rename ships with a migration note, and custom CSS (the only
 * tenant-authored styling) is validated against the contract in the admin
 * console before publish."
 *
 * This file is that interface's first half: the token vocabulary. The second
 * half is `../sections/registry.ts`. Between them they are the entire surface a
 * tenant's `tenant_config.config.branding` may address, and **nothing outside
 * them is configurable** — which is the mechanical form of rule 1, "the shared
 * bundle is never edited for one tenant".
 *
 * ## What a token is
 *
 * A named value that becomes a CSS custom property on `:root`. `color.accent`
 * becomes `--ys-color-accent`. Every rule in `styles.css` reads tokens and never
 * literals, so rung 2 (a tenant's own colours, typefaces, radius, imagery) is a
 * row update with no deploy anywhere — the promise §6.4.3's table makes in its
 * "Framework updates: automatic" column.
 *
 * ## Why unknown tokens warn instead of failing
 *
 * One static build serves every tenant hostname. The admin console can publish
 * a branding row the moment an operator saves it; the bundle that renders it may
 * be the previous release for as long as a CDN cache and a browser tab live. So
 * an unrecognised token is *expected* traffic, not corruption: it is ignored,
 * with one `console.warn` naming it, and the site paints. Refusing the document
 * would take a spa's booking page down because somebody picked a colour the
 * bundle learns about tomorrow.
 *
 * The same reasoning covers a `contract_version` this bundle does not know: warn
 * and render what is understood. A blank page is never the safer failure on a
 * page whose job is to take a booking.
 *
 * ## Why values are validated at all
 *
 * A token value is written into a CSS custom property. A value carrying `;` or
 * `}` could close the declaration and open another, which turns a branding row
 * into a styling injection — small, since the value never becomes script, but
 * not a thing to leave open on a surface where the *tenant* authors the row and
 * an operator publishes it. Validation is by token **type**, and a value that
 * fails keeps the preset's, warns, and paints.
 */

export const THEME_CONTRACT_VERSION = 1;

/** The CSS custom-property prefix. `color.accent` → `--ys-color-accent`. */
export const TOKEN_PREFIX = '--ys-';

export type TokenType = 'color' | 'length' | 'number' | 'font' | 'text' | 'url';

export interface TokenSpec {
  type: TokenType;
  /** What it paints, in the words the admin console's editor will reuse. */
  label: string;
}

/**
 * The v1 token vocabulary — 27 names, and adding a 28th is a contract change.
 *
 * The set is deliberately small. §6.4.3's honest sales framing is that "good
 * typography, their photography, their words do more than bespoke code ever
 * does", and a hundred tokens would be a bespoke site with extra steps: every
 * one of them is a thing that can be set badly, a thing the live preview has to
 * show, and a thing a contract bump has to carry forward.
 */
export const TOKENS = {
  /* Colour — eleven, because a page needs two grounds, three inks and an accent
     that has to be legible *against* something. */
  'color.ink': { type: 'color', label: 'Text' },
  'color.ink-soft': { type: 'color', label: 'Secondary text' },
  'color.ink-faint': { type: 'color', label: 'Quiet text' },
  'color.paper': { type: 'color', label: 'Page background' },
  'color.surface': { type: 'color', label: 'Card background' },
  'color.surface-alt': { type: 'color', label: 'Alternate band' },
  'color.line': { type: 'color', label: 'Rules and borders' },
  'color.accent': { type: 'color', label: 'Accent' },
  /** Text drawn *on* the accent. A token rather than a computation: contrast is
      a decision a designer makes, not one a browser guesses. */
  'color.accent-ink': { type: 'color', label: 'Text on accent' },
  'color.accent-soft': { type: 'color', label: 'Accent wash' },
  'color.danger': { type: 'color', label: 'Warnings' },

  /* Type — the two slots §6.4.3 names ("two typefaces from a curated set"),
     plus the four values that make a pairing look like a pairing rather than
     like two fonts. */
  'font.display': { type: 'font', label: 'Display typeface' },
  'font.body': { type: 'font', label: 'Body typeface' },
  'font.size-base': { type: 'length', label: 'Base text size' },
  'font.size-display': { type: 'length', label: 'Headline size' },
  'font.weight-display': { type: 'number', label: 'Headline weight' },
  'font.leading': { type: 'number', label: 'Line height' },
  'font.tracking-display': { type: 'length', label: 'Headline letter-spacing' },
  'font.case-display': { type: 'text', label: 'Headline capitalisation' },

  /* Shape */
  'radius.sm': { type: 'length', label: 'Small radius' },
  'radius.md': { type: 'length', label: 'Radius' },
  'radius.lg': { type: 'length', label: 'Large radius' },

  /* Space — a scale rather than a list of paddings: one rhythm to move. */
  'space.unit': { type: 'length', label: 'Spacing unit' },
  'space.section': { type: 'length', label: 'Space between sections' },
  'space.measure': { type: 'length', label: 'Content width' },

  /* Imagery and elevation */
  'image.logo': { type: 'url', label: 'Logo' },
  'image.hero': { type: 'url', label: 'Hero image' },
  'shadow.card': { type: 'text', label: 'Card shadow' },
} as const satisfies Record<string, TokenSpec>;

export type TokenName = keyof typeof TOKENS;
export type TokenSet = Record<TokenName, string>;

export const TOKEN_NAMES = Object.keys(TOKENS) as TokenName[];

export function isTokenName(name: string): name is TokenName {
  return Object.prototype.hasOwnProperty.call(TOKENS, name);
}

/** `color.accent` → `--ys-color-accent`. */
export function cssVariable(name: TokenName): string {
  return `${TOKEN_PREFIX}${name.replace(/\./g, '-')}`;
}

/* ------------------------------------------------------------------ */
/* Value validation, by type                                           */
/* ------------------------------------------------------------------ */

/**
 * Characters that would let a value escape its declaration. Checked first for
 * every type, so no per-type pattern has to remember it.
 */
const UNSAFE = /[;{}<>\\]|\/\*|url\s*\(|expression\s*\(|@import/i;

const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const COLOR_FN = /^(?:rgb|rgba|hsl|hsla|oklch|color-mix)\([^()]*\)$/i;
const LENGTH = /^-?(?:\d+\.?\d*|\.\d+)(?:px|rem|em|%|ch|vw|vh|pt)$/;
/** A font *stack*: names, quotes, commas, and the generic families. */
const FONT_STACK = /^[a-z0-9 '",._-]+$/i;
const PLAIN_TEXT = /^[a-z0-9 .,'"()#%/:_-]+$/i;

/**
 * Images are references, not values: the token holds a bare URL and the
 * stylesheet wraps it in `url(…)`. Only `https:` and inline `data:image/`
 * survive — an `http:` reference would be mixed content on a page the LB serves
 * over TLS, and anything else is a scheme with no business in a background.
 */
function isSafeImageUrl(value: string): boolean {
  if (/^data:image\/(png|jpeg|jpg|gif|webp|avif|svg\+xml);base64,[a-z0-9+/=]+$/i.test(value)) {
    return true;
  }
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/** `true` when `value` may be written into the custom property for `name`. */
export function isValidTokenValue(name: TokenName, value: string): boolean {
  if (value.length === 0 || value.length > 400) return false;
  if (UNSAFE.test(value)) return false;

  switch (TOKENS[name].type) {
    case 'color':
      return HEX.test(value) || COLOR_FN.test(value);
    case 'length':
      return LENGTH.test(value) || value === '0';
    case 'number':
      return /^\d+\.?\d*$/.test(value);
    case 'font':
      return FONT_STACK.test(value);
    case 'url':
      return isSafeImageUrl(value);
    case 'text':
      return PLAIN_TEXT.test(value);
  }
}
